import { Component, Input, OnInit, OnDestroy, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormInlineErrorComponent, State } from '@k12/forms';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NgxMaskDirective } from 'ngx-mask';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CountiesService, CountyResponse } from '@k12/school-services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-household-contact-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    NgxMaskDirective,
    FormInlineErrorComponent
  ],
  templateUrl: './household-contact-info.component.html'
})
export class HouseholdContactInfoComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;
  @Input() states: State[] = [];
  private readonly countiesService = inject(CountiesService);

  counties: CountyResponse[] = [];
  isNorthCarolina = signal(false);
  filteredStates!: Observable<State[]>;
  filteredCounties!: Observable<CountyResponse[]>;
  private countyValidityTimeoutId?: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    clearTimeout(this.countyValidityTimeoutId);
  }

  ngOnInit() {
    this.filteredStates = this.formGroup.get('state')!.valueChanges.pipe(
      startWith(this.formGroup.get('state')!.value),
      map((value) => this._filterStates(value || ''))
    );

    this.formGroup
      .get('state')!
      .valueChanges.pipe(startWith(this.formGroup.get('state')!.value))
      .subscribe((state) => {
        let stateCode: string | undefined;

        if (typeof state === 'object' && state?.code) {
          stateCode = state.code;
        } else if (typeof state === 'string') {
          const matched = this.states.find((s) => s.code === state || s.name === state);
          stateCode = matched?.code;
        }

        this.isNorthCarolina.set(stateCode === 'NC');

        if (this.isNorthCarolina() && this.counties.length === 0) {
          this.loadCounties('NC');
        }

        if (!this.isNorthCarolina()) {
          this.formGroup.patchValue({ county: '' }, { emitEvent: false });
        }

        if (this.isNorthCarolina()) {
          this.countyValidityTimeoutId = setTimeout(() => {
            this.formGroup.get('county')?.updateValueAndValidity({ emitEvent: true });
          });
        }
      });

    this.filteredCounties = this.formGroup.get('county')!.valueChanges.pipe(
      startWith(this.formGroup.get('county')!.value || ''),
      map((value) => this._filterCounties(value || ''))
    );
  }

  private _filterStates(value: string | State): State[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() || '';
    return this.states.filter((state) => state.name.toLowerCase().includes(filterValue));
  }

  private _filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  displayStateName(state: State): string {
    return state?.name || '';
  }
  private loadCounties(state: string): void {
    this.countiesService.getCountiesByState(state).subscribe({
      next: (data: CountyResponse[]) => {
        this.counties = data;

        this.formGroup.get('county')!.updateValueAndValidity({ emitEvent: true });
      },
      error: () => {
        this.counties = [];
      }
    });
  }
}
